import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import BounceLoader from 'react-spinners/BounceLoader';
import moment from 'moment';

// Components
import Progress from './Progress';
import TableData from './TableData';
import Graphics from './Graphics';
import SelectButton from '../utils/SelectButton';
import CycleDatesInfo from '../utils/CycleDatesInfo';
import OrganizationFilters from '../utils/OrganizationFilters';
import AreaFilters from '../utils/AreaFilters';

// Images
import imgGif from '../../images/overview-img.svg';

// Hooks
import useDidMountEffect from '../../hooks/useDidMountEffect';

// Actions
import {
	get_organizations, get_workspaces,
	get_modules, get_cycles, get_areas, get_areas_mine, set_org_filters, get_progress_options
} from '../../actions/organizationActions';
import { get_monthly_graphic_data, get_daily_graphic_data, get_progress, get_progress_mine, get_indicators_progress } from '../../actions/overviewActions';
import { get_org_reports } from '../../actions/reportsActions';
import { get_self_files_latest } from '../../actions/selfFilesActions';

// Utils
import isEmpty from '../../utils/isEmpty';

const Overview = () => {
	const dispatch = useDispatch();

	const _ls = { ...localStorage };

	const { user, isAdminUser, authChecked } = useSelector(state => state.auth);
	const { global_data } = useSelector(state => state.global);
	
	const { ui_loading } = useSelector(state => state.overview);

	const { organizations, workspaces, modules, cycles, areas, areasLoading, progress_options } = useSelector(state => state.organization);

	const { org_reports } = useSelector(state => state.reports);

	const { latestFile } = useSelector(state => state.selfFiles);

	const { app_config } = useSelector(state => state.apps);

	const { staff, staffChecked } = useSelector(state => state.staff);

	const [orgFilters, setOrgFilters] = useState({ organization: '', workspace: '', module: '', cycle: '' });
	const [areaFilters, setAreaFilters] = useState({ region: '', zone: '', depot: '', group: '' });
	const [areaLabels, setAreaLabels] = useState({ region: '', zone: '', depot: '', group: '' });
	const [progressFilters, setProgressFilters] = useState({ parent: {}, child: {} });

	const [cycle, setCycle] = useState({});
	const [dayToSearch, setDayToSearch] = useState('');
	const [showStaffData, setShowStaffData] = useState(false);

	const [disableInputs, setDisableInputs] = useState(true);
	const [displayData, setDisplayData] = useState(false);

	const [orgReports, setOrgReports] = useState([]);

	const [pendingSearch, setPendingSearch] = useState(false);
	const skipHandleRequestsRef = useRef(false);

	const [initialAreas, setInitialAreas] = useState(null);
	const areasDispatchedForRef = useRef(null);

	useEffect(() => {
		dispatch(get_organizations());
	}, []);

	useEffect(() => {
		let _organization = '';

		if (organizations.length <= 1) {
			_organization = user.organization;
		}
		else {
			_organization = _ls?.organization;
		}
		
		setOrgFilters({
			...orgFilters,
			organization: _organization,
			workspace: _ls?.workspace || '',
			module: _ls?.module || '',
			cycle: _ls?.cycle || ''
		});
	}, [organizations]);

	useDidMountEffect(() => {
		if (orgFilters.organization !== '') {
			dispatch(get_workspaces({ organization: orgFilters.organization }));
		}
	}, [orgFilters.organization]);

	const onChangeOrgFilters = (e, name) => {
		let filters = {...orgFilters};
		let keys = Object.keys(filters);

		let idx = keys.indexOf(name);
		for (let index = idx; index < keys.length; index++) {
			const key = keys[index];
			filters[key] = '';
			localStorage.setItem(key, '');
		}

		if (e !== null){
			filters[name] = e.value;
			localStorage.setItem(name, e.value);
		}

		skipHandleRequestsRef.current = true;
		setOrgFilters(filters);
		setAreaFilters({ region: '', zone: '', depot: '', group: '' });
		setAreaLabels({ region: '', zone: '', depot: '', group: '' });
		['region', 'zone', 'depot', 'group'].forEach(k => localStorage.setItem(k, ''));
	}

	const onChangeAreaFilters = (e, name) => {
		let filters = {...areaFilters};
		let labels = {...areaLabels};
		let keys = Object.keys(filters);
		
		if (e === null){
			let idx = keys.indexOf(name);
			for (let index = idx; index < keys.length; index++) {
				const key = keys[index];
				filters[key] = '';
				labels[key] = '';
				localStorage.setItem(key, '');
			}
		}
		else {
			filters[name] = e.value;
			labels[name] = e.label;
			localStorage.setItem(name, e.value);
		}

		setAreaFilters(filters);
		setAreaLabels(labels);
	}
	
	useDidMountEffect(() => {
		setDisplayData(false);

		if (orgFilters.organization !== '') {
			dispatch(get_modules({ organization: orgFilters.organization, workspace: orgFilters.workspace }));
		}

		if (orgFilters.workspace !== '') {
			if (orgFilters.module !== '') {
				dispatch(get_cycles({ workspace: orgFilters.workspace, module: orgFilters.module }));
			}
			dispatch(get_progress_options({ workspace: orgFilters.workspace }));
		}
	}, [orgFilters]);

	useDidMountEffect(() => {
		if (orgFilters.module !== '' && orgFilters.cycle !== ''){
			setDisableInputs(false);
		}
		else {
			setDisableInputs(true);
			setDisplayData(false);
		}
	}, [orgFilters]);

	useDidMountEffect(() => {
		if (!authChecked) return;
		if (orgFilters.cycle !== '') {
			const areasAction = isAdminUser ? get_areas : get_areas_mine;
			areasDispatchedForRef.current = `${orgFilters.workspace}|${orgFilters.cycle}`;
			dispatch(areasAction(orgFilters.workspace, orgFilters.cycle, areaFilters));
		}
	}, [orgFilters, areaFilters, isAdminUser, authChecked]);

	useEffect(() => {
		const currentStaffType = staff?.staff_type ?? 0;
		const levels = [
			{ key: 'region', source: areas?.regions, scopeMin: 4 },
			{ key: 'zone', source: areas?.zones, scopeMin: 8 },
			{ key: 'depot', source: areas?.depots, scopeMin: 16 },
			{ key: 'group', source: areas?.groups, scopeMin: 32 }
		];

		const preselectedFilters = {};
		const preselectedLabels = {};

		levels.forEach(({ key, source, scopeMin }) => {
			if (currentStaffType < scopeMin) return;
			if (!Array.isArray(source) || source.length !== 1) return;

			const value = source[0]?._id?.$oid;
			const label = source[0]?.name;
			if (!value) return;
			if (areaFilters[key] === value) return;

			preselectedFilters[key] = value;
			preselectedLabels[key] = label ?? '';
			localStorage.setItem(key, value);
		});

		if (Object.keys(preselectedFilters).length === 0) return;

		skipHandleRequestsRef.current = true;
		setAreaFilters(prev => ({ ...prev, ...preselectedFilters }));
		setAreaLabels(prev => ({ ...prev, ...preselectedLabels }));
	}, [areas, staff]);

	useDidMountEffect(() => {
		setInitialAreas(null);
		areasDispatchedForRef.current = null;
	}, [orgFilters.workspace, orgFilters.module, orgFilters.cycle]);

	useEffect(() => {
		if (initialAreas) return;
		if (areasLoading) return;
		if (areasDispatchedForRef.current !== `${orgFilters.workspace}|${orgFilters.cycle}`) return;
		if (!areas || typeof areas !== 'object' || Array.isArray(areas)) return;

		const noFiltersApplied = Object.values(areaFilters).every(v => v === '');
		if (!noFiltersApplied) return;

		setInitialAreas(areas);
	}, [areas, areaFilters, initialAreas, areasLoading, orgFilters.workspace, orgFilters.cycle]);

	useEffect(() => {
		if (!pendingSearch) return;
		if (!staffChecked) return;
		if (areasLoading) return;
		if (!areas || typeof areas !== 'object' || Array.isArray(areas)) return;

		const currentStaffType = staff?.staff_type ?? 0;
		const scopeMap = { regions: 4, zones: 8, depots: 16, groups: 32 };
		const hasPendingPreselection = Object.entries(scopeMap).some(([key, scopeMin]) => {
			if (currentStaffType < scopeMin) return false;
			const arr = areas?.[key];
			if (!Array.isArray(arr) || arr.length !== 1) return false;
			const id = arr[0]?._id?.$oid;
			if (!id) return false;
			const filterKey = key.slice(0, -1);
			return areaFilters[filterKey] !== id;
		});
		if (hasPendingPreselection) return;

		handleRequests();
		setPendingSearch(false);
	}, [pendingSearch, areasLoading, areas, areaFilters, staff, staffChecked]);

	const onChangeProgressFilters = (e, name) => {
		let parentFilters = {...progressFilters.parent};
		let childFilters = {...progressFilters.child};

		if (e !== null) {
			if (parentFilters.hasOwnProperty('child_tables')) { // parent was a child filter 
				// change from parent to child
				childFilters = {
					child: parentFilters.option,
					child_value: parentFilters.option_value
				};
			}

			// set parent
			parentFilters = { option: name, option_value: e.value }
		}
		else {
			const isChildEmpty = isEmpty(childFilters);

			if (!isChildEmpty) { // if child filter is not empty
				// change from child to parent
				parentFilters = {
					option: childFilters?.child,
					option_value: childFilters?.child_value,
					child_tables: true
				};
				childFilters = {};
			}
			else { // if child is empty
				parentFilters = {};
				childFilters = {};
			}
		}

		setProgressFilters({
			parent: parentFilters, child: childFilters
		});
	}

	useDidMountEffect(() => {
		const day = moment(dayToSearch, 'YYYY-MM-DD');

		let dayDuration = {
			start: Math.trunc(day.startOf('day').valueOf() / 1000),
			end: Math.trunc(day.endOf('day').valueOf() / 1000)
		}

		if (dayToSearch !== '') {
			dispatch(get_daily_graphic_data(orgFilters, areaFilters, dayDuration));
		}
	}, [dayToSearch]);

	const handleRequests = () => {
		let cycle = cycles.find(cycle => cycle._id.$oid === orgFilters.cycle);
		if (!cycle) return;

		setDisplayData(true);
		setCycle(cycle);

		dispatch(set_org_filters(orgFilters));

		let cycleDuration = {
			start: cycle.start.$date / 1000,
			end: cycle.end.$date / 1000
		}

		let day = null;
		let dayDuration = {};

		if (cycle.status === 5) { // cycle has ended
			day = moment(cycle.end.$date);
			dayDuration = {
				start: Math.trunc(day.startOf('day').valueOf() / 1000),
				end: Math.trunc(day.endOf('day').valueOf() / 1000)
			}
			setDayToSearch(moment(cycle.end.$date).format('YYYY-MM-DD'));
		}
		else { // cycle is available
			day = moment();
			dayDuration = {
				start: Math.trunc(day.startOf('day').valueOf() / 1000),
				end: Math.trunc(day.endOf('day').valueOf() / 1000)
			}
			setDayToSearch(moment().format('YYYY-MM-DD'));
		}

		// Graphics data
		dispatch(get_monthly_graphic_data(orgFilters, areaFilters, cycleDuration));
		dispatch(get_daily_graphic_data(orgFilters, areaFilters, dayDuration));

		// Progress data
		const progressAction = isAdminUser ? get_progress : get_progress_mine;
		dispatch(progressAction(orgFilters, areaFilters, progressFilters, showStaffData));
		// dispatch(get_indicators_progress(orgFilters, areaFilters, progressFilters));

		// Organization reports
		dispatch(get_org_reports({
			app: process.env.REACT_APP_ALIAS,
			organization: orgFilters.organization,
			workspace: orgFilters.workspace,
			module: orgFilters.module
		}));

		// Self files
		dispatch(get_self_files_latest(orgFilters));
	}

	useDidMountEffect(() => {
		if (skipHandleRequestsRef.current) {
			skipHandleRequestsRef.current = false;
			return;
		}
		handleRequests();
	}, [areaFilters, progressFilters, showStaffData]);

	useDidMountEffect(() => {
		if (displayData && org_reports.length > 0) {
			// Flatten all reportsExtra entries from app_config into a single lookup map.
			// app_config is an array; each item may contain data.reportsExtra keyed by report _id.
			const reportsExtra = Array.isArray(app_config)
				? app_config.reduce((acc, config) => ({ ...acc, ...config?.data?.reportsExtra }), {})
				: {};

			// Filter reports to those matching the currently selected organization/workspace/module,
			// then enrich each one with its extra body (extra_fields) if a match exists in app_config.
			// Reports with a `body` property will be sent via POST in download_report; the rest stay as GET.
			const filteredOrgReports = org_reports
				.filter(report =>
					orgFilters.organization === report.organization.$oid &&
					orgFilters.workspace === report.workspace.$oid &&
					orgFilters.module === report.module.$oid
				)
				.map(report => {
					const extra = reportsExtra[report._id.$oid];
					return extra?.body ? { ...report, body: extra.body } : report;
				});

			setOrgReports(filteredOrgReports);
		}
	}, [org_reports]);

	// Store latest file info in localStorage
	useDidMountEffect(() => {
		if (latestFile && latestFile._id) {
			const currentFileInfo = JSON.parse(localStorage.getItem('latestFileInfo') || '{}');
			const newId = latestFile._id.$oid;

			// If same id, don't update
			if (currentFileInfo.id === newId) return;

			// Validate load_type is 1 or 3
			if (latestFile.load_type !== 1 && latestFile.load_type !== 3) return;

			// Validate status is 5
			if (latestFile.status !== 5) return;

			// Validate hierarchy has all 4 required values
			const requiredHierarchy = ['region', 'zone', 'depot', 'group'];
			const hasValidHierarchy = Array.isArray(latestFile.hierarchy) &&
				latestFile.hierarchy.length === 4 &&
				requiredHierarchy.every((item, index) => latestFile.hierarchy[index] === item);

			if (!hasValidHierarchy) return;

			const fileInfo = {
				id: newId,
				name: latestFile.name,
				hierarchy: latestFile.hierarchy
			};
			localStorage.setItem('latestFileInfo', JSON.stringify(fileInfo));
		}
	}, [latestFile]);

	const searchInfo = (e) => {
		e.preventDefault();
		setPendingSearch(true);
	}

	const staffType = isAdminUser ? 0 : (staff?.staff_type ?? 0);
	const isEmptyLevel = (arr) => !Array.isArray(arr) || arr.length === 0;
	const areasForVisibility = initialAreas || areas;
	const hiddenLevels = {
		region: staffType >= 4 || isEmptyLevel(areasForVisibility?.regions),
		zone: staffType >= 8 || isEmptyLevel(areasForVisibility?.zones),
		depot: staffType >= 16 || isEmptyLevel(areasForVisibility?.depots),
		group: staffType >= 32 || isEmptyLevel(areasForVisibility?.groups)
	};
	const allAreasHidden = hiddenLevels.region && hiddenLevels.zone && hiddenLevels.depot && hiddenLevels.group;

	return ( 
		<div className='m-3 pb-5'>
			<h2 className='text-center'>{global_data?.overview?.title}</h2>
			<p className='text-center'>{global_data?.overview?.description}</p>

			{/* Organization Info Filters */}
			<OrganizationFilters
				orgFilters={orgFilters}
				onChangeOrgFilters={onChangeOrgFilters}
				organizations={organizations}
				workspaces={workspaces}
				modules={modules}
				cycles={cycles}
			/>

			<div className='d-grid gap-2 col-2 mx-auto text-center mt-4'>
				<button className='btn submit-btn' type='button' disabled={disableInputs} onClick={searchInfo}>
					{(ui_loading || pendingSearch)
						? <div className='d-flex row align-items-center justify-content-center'>
								<BounceLoader color='#ffffff' size={20}/>
							</div>
						: <div>{global_data?.actionButtons?.search}</div>
					}
				</button>
			</div>

			{displayData
				? <Fragment>
						{/* <hr/> */}
						{!allAreasHidden && (
							<Fragment>
								<h4 className='text-center mt-4 mb-3'>{global_data?.overview?.filterBy}:</h4>

								{/* Area Filters */}
								<AreaFilters
									areaFilters={areaFilters}
									areaLabels={areaLabels}
									onChangeAreaFilters={onChangeAreaFilters}
									areas={areas}
									disableInputs={disableInputs}
									areasLoading={areasLoading}
									hiddenLevels={hiddenLevels}
								/>
								<div className='d-flex align-items-center justify-content-center mb-3'>
									<div className="form-check form-switch">
										<input
											className="form-check-input"
											type="checkbox"
											role="switch"
											id="flexSwitchCheckChecked"
											checked={showStaffData}
											onChange={(e) => {
												e.preventDefault();
												setShowStaffData(!showStaffData);
											}}
										/>
										<label className="form-check-label" htmlFor="flexSwitchCheckChecked">{global_data?.organizationTerms?.staffUsers}</label>
									</div>
								</div>
								<hr className='hr-style'/>
							</Fragment>
						)}

						{/* Options Filters */}
						{progress_options.length > 0 &&
							<Fragment>
								<div className='d-flex row align-items-center justify-content-center mt-4 mb-4'>
									<div className='text-center mb-2'>
										<i className='bi bi-gear me-2'></i>{global_data?.overview?.optionFiltersTitle}
									</div>
									{progress_options.map((p_opt, idx) => (
										<Fragment key={idx}>
											<SelectButton
												label={p_opt.name}
												options={p_opt.values.length !== 0 ? p_opt.values : []}
												name={p_opt._id}
												onChange={onChangeProgressFilters}
												disable={disableInputs}
												orgFilter={false}
												optFilter={true}
											/>
										</Fragment>
									))}
									<div id='progressOptionsHelp' className='form-text text-center mb-4'>
										{global_data?.overview?.optionFiltersLegend}
									</div>
								</div>
								<hr className='hr-style'/>
							</Fragment>
						}

						{/* Cycle dates info */}
						<CycleDatesInfo cycle={cycle} />
						
						{/* Bar charts */}
						<Graphics cycle={cycle} dayToSearch={dayToSearch} setDayToSearch={setDayToSearch} />
						<hr className='hr-style'/>
			
						{/* Progress */}
						<Progress progressFilters={progressFilters} setProgressFilters={setProgressFilters} />
						<hr className='hr-style'/>
			
						{/* Progress table */}
						<TableData
							cycle={cycle}
							orgFilters={orgFilters}
							areaFilters={areaFilters}
							areaLabels={areaLabels}
							progressFilters={progressFilters}
							org_reports={orgReports}
							showStaffData={showStaffData}
						/>
					</Fragment>
				: <img className='image' src={imgGif} alt='Overview'/> 
			}
		</div>
	);
}

export default Overview;
